<template>
  <div class="page-card">
    <h2 class="page-title">角色管理</h2>

    <div class="toolbar">
      <div />
      <el-button type="primary" @click="openRoleDialog()">新增角色</el-button>
    </div>

    <el-table :data="list" border>
      <el-table-column prop="roleName" label="角色名称" />
      <el-table-column prop="roleCode" label="角色编码" />
      <el-table-column prop="roleDesc" label="角色描述" />
      <el-table-column prop="dataScopeType" label="数据范围">
        <template slot-scope="{ row }">
          {{ dataScopeText(row.dataScopeType) }}
        </template>
      </el-table-column>
      <el-table-column prop="roleStatus" label="状态" width="120">
        <template slot-scope="{ row }">
          <el-tag :type="row.roleStatus === 1 ? 'success' : 'info'">
            {{ row.roleStatus === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openRoleDialog(row)">编辑</el-button>
          <el-button type="text" @click="openPermissionDialog(row)">配置权限</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="roleForm.id ? '编辑角色' : '新增角色'" :visible.sync="roleVisible" width="520px">
      <el-form ref="roleRef" :model="roleForm" :rules="roleRules" label-width="100px">
        <el-form-item label="角色名称" prop="roleName">
          <el-input v-model="roleForm.roleName" />
        </el-form-item>
        <el-form-item label="角色编码" prop="roleCode">
          <el-input v-model="roleForm.roleCode" />
        </el-form-item>
        <el-form-item label="角色描述">
          <el-input v-model="roleForm.roleDesc" type="textarea" />
        </el-form-item>
        <el-form-item label="数据范围" prop="dataScopeType">
          <el-select v-model="roleForm.dataScopeType" style="width: 100%">
            <el-option label="全平台" :value="1" />
            <el-option label="指定城市/区域" :value="2" />
            <el-option label="指定业务线" :value="3" />
            <el-option label="仅本人处理数据" :value="4" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="roleStatus">
          <el-radio-group v-model="roleForm.roleStatus">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="roleVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRole">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="权限配置" :visible.sync="permissionVisible" width="720px">
      <el-tree
        ref="treeRef"
        :data="permissionTree"
        show-checkbox
        node-key="id"
        default-expand-all
        :props="{ label: 'permissionName', children: 'children' }"
      />
      <span slot="footer">
        <el-button @click="permissionVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPermissions">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { createAdminRole, getAdminRoles, getRolePermissionIds, saveRolePermissions, updateAdminRole } from '../../../api/adminRole'
import { getPermissionTree } from '../../../api/adminPermission'

function getRoleForm() {
  return {
    id: '',
    roleName: '',
    roleCode: '',
    roleDesc: '',
    dataScopeType: 1,
    roleStatus: 1
  }
}

export default {
  name: 'AdminRolePage',
  data() {
    return {
      list: [],
      permissionTree: [],
      roleVisible: false,
      permissionVisible: false,
      currentRole: null,
      roleForm: getRoleForm(),
      roleRules: {
        roleName: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
        roleCode: [{ required: true, message: '请输入角色编码', trigger: 'blur' }],
        dataScopeType: [{ required: true, message: '请选择数据范围', trigger: 'change' }],
        roleStatus: [{ required: true, message: '请选择状态', trigger: 'change' }]
      }
    }
  },
  created() {
    this.fetchData()
    this.fetchPermissions()
  },
  methods: {
    dataScopeText(type) {
      if (type === 1) return '全平台'
      if (type === 2) return '指定城市/区域'
      if (type === 3) return '指定业务线'
      if (type === 4) return '仅本人处理数据'
      return '-'
    },
    async fetchData() {
      this.list = await getAdminRoles()
    },
    async fetchPermissions() {
      this.permissionTree = await getPermissionTree()
    },
    openRoleDialog(row) {
      this.roleVisible = true
      this.roleForm = row ? { ...getRoleForm(), ...row, id: row.id ? String(row.id) : '' } : getRoleForm()
    },
    normalizeId(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      return String(value)
    },
    submitRole() {
      this.$refs.roleRef.validate(async valid => {
        if (!valid) return
        const payload = {
          ...this.roleForm,
          id: this.normalizeId(this.roleForm.id)
        }
        if (payload.id) {
          await updateAdminRole(payload.id, payload)
        } else {
          await createAdminRole(payload)
        }
        this.roleVisible = false
        this.fetchData()
      })
    },
    async openPermissionDialog(row) {
      this.currentRole = row ? { ...row, id: row.id ? String(row.id) : '' } : null
      this.permissionVisible = true
      const checkedKeys = this.currentRole && this.currentRole.id
        ? await getRolePermissionIds(this.currentRole.id)
        : []
      this.$nextTick(() => {
        this.$refs.treeRef.setCheckedKeys(checkedKeys || [])
      })
    },
    async submitPermissions() {
      const checkedKeys = this.$refs.treeRef.getCheckedKeys().map(item => String(item))
      await saveRolePermissions(this.currentRole.id, { permissionIds: checkedKeys })
      this.permissionVisible = false
    }
  }
}
</script>
